import DOMPurify from 'dompurify';

export function analyzeAndSanitize(dirty) {
  const seen = { hosts: new Set(), tags: new Set(), stripped: [] };
  
  // Store the original content
  const originalContent = dirty || '';
  
  console.log('=== SANITIZATION START ===');
  console.log('Original content length:', originalContent.length);
  console.log('First 500 chars:', originalContent.substring(0, 500));
  
  // List of trusted domains for iframes
  const trustedDomains = [
    'bandcamp.com',
    'youtube.com',
    'youtube-nocookie.com',
    'spotify.com',
    'soundcloud.com',
    'vimeo.com',
    'dailymotion.com',
    'twitch.tv',
    'facebook.com',
    'instagram.com',
    'twitter.com',
    'codepen.io',
    'codesandbox.io'
  ];
  
  // First, find and preserve ALL raw-html-embed divs with their content
  const rawHtmlEmbedRegex = /<div class="raw-html-embed">[\s\S]*?<\/div>/gi;
  const embedMatches = originalContent.match(rawHtmlEmbedRegex) || [];
  
  console.log('Found raw-html-embed divs:', embedMatches.length);
  
  const trustedEmbeds = [];
  const embedPlaceholders = new Map();
  
  // Process each raw-html-embed div
  embedMatches.forEach((embedDiv, index) => {
    console.log(`Processing embed ${index}:`, embedDiv.substring(0, 200));
    
    // Check if this embed contains an iframe (handle multiline)
    const iframeMatch = embedDiv.match(/<iframe[\s\S]*?src=["']([^"']+)["'][\s\S]*?<\/iframe>/i);
    
    if (iframeMatch) {
      const iframeSrc = iframeMatch[1];
      console.log(`Found iframe in embed ${index} with src:`, iframeSrc);
      
      try {
        const url = new URL(iframeSrc);
        const hostname = url.hostname.toLowerCase();
        const isTrusted = trustedDomains.some(domain => hostname.includes(domain));
        
        seen.hosts.add(hostname);
        
        if (isTrusted) {
          console.log(`✓ Trusted embed from: ${hostname}`);
          // Create a unique placeholder that won't be stripped by DOMPurify
          const placeholder = `<div data-embed-placeholder="${index}_${Date.now()}">EMBED_PLACEHOLDER_${index}</div>`;
          embedPlaceholders.set(placeholder, embedDiv);
          trustedEmbeds.push({ placeholder, content: embedDiv, index });
          seen.tags.add('IFRAME');
        } else {
          console.log(`✗ Untrusted embed from: ${hostname}`);
          seen.stripped.push({ tag: 'IFRAME', reason: 'untrusted-domain', src: iframeSrc });
        }
      } catch (e) {
        console.warn('Invalid iframe URL:', iframeSrc, e);
        seen.stripped.push({ tag: 'IFRAME', reason: 'invalid-url', src: iframeSrc });
      }
    } else {
      // Even if there's no iframe detected, preserve the raw-html-embed structure
      // It might contain other embedded content or the regex might have missed it
      console.log(`Embed ${index} - preserving as-is (iframe detection may have failed)`);
      const placeholder = `<div data-embed-placeholder="${index}_${Date.now()}">EMBED_PLACEHOLDER_${index}</div>`;
      embedPlaceholders.set(placeholder, embedDiv);
      trustedEmbeds.push({ placeholder, content: embedDiv, index });
    }
  });
  
  // Replace all trusted embeds with placeholders
  let contentWithPlaceholders = originalContent;
  trustedEmbeds.forEach(({ content }) => {
    // Find the exact position and replace
    const embedIndex = contentWithPlaceholders.indexOf(content);
    if (embedIndex !== -1) {
      const placeholder = Array.from(embedPlaceholders.entries()).find(([p, c]) => c === content)?.[0];
      if (placeholder) {
        contentWithPlaceholders = contentWithPlaceholders.substring(0, embedIndex) + 
                                  placeholder + 
                                  contentWithPlaceholders.substring(embedIndex + content.length);
      }
    }
  });
  
  console.log('Content after placeholder replacement:', contentWithPlaceholders.substring(0, 500));
  
  // Now also handle standalone iframes (not in raw-html-embed divs)
  const standaloneIframeRegex = /<iframe[\s\S]*?<\/iframe>/gi;
  const standaloneIframes = contentWithPlaceholders.match(standaloneIframeRegex) || [];
  const trustedStandaloneIframes = new Map();
  
  standaloneIframes.forEach((iframeHtml, index) => {
    // Skip if this is part of a placeholder
    if (iframeHtml.includes('data-embed-placeholder')) return;
    
    const srcMatch = iframeHtml.match(/src=["']([^"']+)["']/i);
    if (srcMatch) {
      const src = srcMatch[1];
      console.log('Found standalone iframe:', src);
      
      try {
        const url = new URL(src);
        const hostname = url.hostname.toLowerCase();
        const isTrusted = trustedDomains.some(domain => hostname.includes(domain));
        
        seen.hosts.add(hostname);
        
        if (isTrusted) {
          console.log(`✓ Trusted standalone iframe from: ${hostname}`);
          const placeholder = `<div data-iframe-placeholder="${index}_${Date.now()}">IFRAME_${index}</div>`;
          trustedStandaloneIframes.set(placeholder, iframeHtml);
          contentWithPlaceholders = contentWithPlaceholders.replace(iframeHtml, placeholder);
          seen.tags.add('IFRAME');
        } else {
          console.log(`✗ Untrusted standalone iframe from: ${hostname}`);
          seen.stripped.push({ tag: 'IFRAME', reason: 'untrusted-domain', src });
        }
      } catch (e) {
        console.warn('Invalid standalone iframe URL:', src);
        seen.stripped.push({ tag: 'IFRAME', reason: 'invalid-url', src });
      }
    }
  });
  
  // Configure DOMPurify to preserve our placeholders
  const clean = DOMPurify.sanitize(contentWithPlaceholders, {
    ADD_TAGS: ['iframe'],
    ADD_ATTR: ['allowfullscreen', 'frameborder', 'scrolling', 'allow', 'loading', 'seamless', 
                'data-embed-placeholder', 'data-iframe-placeholder'],
    ALLOW_DATA_ATTR: true,
    KEEP_CONTENT: true,
    FORCE_BODY: true,
    ALLOWED_TAGS: ['div', 'span', 'p', 'br', 'strong', 'em', 'u', 's', 'a', 'img',
                   'figure', 'figcaption',
                   'ul', 'ol', 'li', 'blockquote', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
                   'table', 'thead', 'tbody', 'tr', 'td', 'th', 'caption',
                   'pre', 'code', 'hr', 'sub', 'sup', 'b', 'i'],
    ALLOWED_ATTR: ['href', 'src', 'alt', 'title', 'width', 'height', 'class', 'id', 
                   'style', 'target', 'rel', 'data-embed-placeholder', 'data-iframe-placeholder'],
    FORBID_TAGS: ['script', 'style'],
    FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onmouseout', 'onmousemove',
                  'onmouseenter', 'onmouseleave', 'onchange', 'oninput', 'onsubmit']
  });
  
  console.log('Sanitized content:', clean.substring(0, 500));
  
  // Restore all placeholders
  let finalContent = clean;
  
  // Restore embed divs
  embedPlaceholders.forEach((content, placeholder) => {
    // Look for the placeholder div in the sanitized content
    const placeholderPattern = placeholder.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(placeholderPattern, 'g');
    
    if (finalContent.includes(placeholder) || regex.test(finalContent)) {
      console.log(`Restoring embed placeholder: ${placeholder.substring(0, 50)}`);
      finalContent = finalContent.replace(regex, content);
      finalContent = finalContent.replace(placeholder, content);
    } else {
      // Try to find just the data attribute part
      const dataMatch = placeholder.match(/data-embed-placeholder="([^"]+)"/);
      if (dataMatch) {
        const searchPattern = `data-embed-placeholder="${dataMatch[1]}"`;
        const divPattern = new RegExp(`<div[^>]*${searchPattern}[^>]*>.*?</div>`, 'gi');
        if (divPattern.test(finalContent)) {
          console.log(`Restoring embed with pattern match`);
          finalContent = finalContent.replace(divPattern, content);
        }
      }
    }
  });
  
  // Restore standalone iframes
  trustedStandaloneIframes.forEach((content, placeholder) => {
    const placeholderPattern = placeholder.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(placeholderPattern, 'g');
    
    if (finalContent.includes(placeholder) || regex.test(finalContent)) {
      console.log(`Restoring iframe placeholder: ${placeholder.substring(0, 50)}`);
      finalContent = finalContent.replace(regex, content);
      finalContent = finalContent.replace(placeholder, content);
    }
  });
  
  console.log('=== SANITIZATION END ===');
  console.log('Final content length:', finalContent.length);
  console.log('Hosts found:', Array.from(seen.hosts));
  console.log('Tags found:', Array.from(seen.tags));
  console.log('Stripped items:', seen.stripped);
  
  // Final check: count iframes in output
  const finalIframeCount = (finalContent.match(/<iframe/gi) || []).length;
  const finalEmbedCount = (finalContent.match(/<div class="raw-html-embed">/gi) || []).length;
  console.log(`Final counts - Iframes: ${finalIframeCount}, Embed divs: ${finalEmbedCount}`);
  
  return {
    html: finalContent,
    report: {
      hosts: Array.from(seen.hosts),
      tags: Array.from(seen.tags),
      stripped: seen.stripped,
      stats: {
        originalLength: originalContent.length,
        finalLength: finalContent.length,
        embedsPreserved: trustedEmbeds.length,
        iframesPreserved: trustedStandaloneIframes.size,
        finalIframeCount,
        finalEmbedCount
      }
    }
  };
}

// Helper function to check if content has embeds that need preservation
export function hasEmbeds(content) {
  if (!content) return false;
  
  const hasRawHtmlEmbed = /<div class="raw-html-embed">/.test(content);
  const hasIframe = /<iframe/.test(content);
  
  return hasRawHtmlEmbed || hasIframe;
}

// Helper function to extract embed info for debugging
export function extractEmbedInfo(content) {
  if (!content) return [];
  
  const embeds = [];
  const embedRegex = /<div class="raw-html-embed">([\s\S]*?)<\/div>/gi;
  let match;
  
  while ((match = embedRegex.exec(content)) !== null) {
    const embedContent = match[1];
    const iframeMatch = embedContent.match(/src=["']([^"']+)["']/);
    
    embeds.push({
      type: 'raw-html-embed',
      content: embedContent.substring(0, 200),
      iframeSrc: iframeMatch ? iframeMatch[1] : null
    });
  }
  
  return embeds;
}
